import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { setSelectedWorkspace } from "../../redux/workspace/actions";
import { UseCompanyWorkspaces, UseSelectedWorkspace, UseCompanySubWorkspaces, UseCompanyLogo, UseCompanyColors } from "../../hooks/company";
import { UseUserDetails, UseIsAdmin, UseIsPrime, UseIsAffiliate, UseAccess } from "../../hooks/auth";
import { UseUnclaimedCount } from "../../hooks/claims";
import { GET_IMAGE } from "../../helpers/images";
import { ID_TO_ROUTE } from "./navConfig";
import adminFooterRoutes from "../FooterDrawer/adminFooterRoutes";
import playerFooterRoutes from "../FooterDrawer/playerFooterRoutes";
import WorkspaceDialog, { WORKSPACE_ACTION } from "../WorkspaceDrawer/WorkspaceDialog";
import SubWorkspaceDialog, { SUB_WORKSPACE_ACTION } from "../WorkspaceDrawer/SubWorkspace/SubWorkspaceDialog";

/* Section header: lighter weight + smaller so the items lead the read */
const SECTION_HEADER = ({ label, isCollapsed }) => {
  if (isCollapsed) return <div className="h-px my-4 mx-4 bg-white/5" />;
  return (
    <div className="px-4 mt-6 mb-2">
      <span className="text-[9.5px] font-medium uppercase text-white/40" style={{ letterSpacing: "0.14em" }}>{label}</span>
    </div>
  );
};

/* Icons: w-5 h-5, active white, inactive translucent white.
   Lucide line-icon paths, 1.75px stroke, all round caps/joins. */
const RENDER_NAV_ICON = (id, isActive) => {
  const colorClass = isActive ? "text-white" : "text-white/80";
  const baseClasses = `w-[16px] h-[16px] flex-shrink-0 ${colorClass}`;
  const svgProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: baseClasses,
  };
  switch (id) {
    case "company-view":
      // lucide:building-2
      return (
        <svg {...svgProps}>
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
          <path d="M10 6h4" />
          <path d="M10 10h4" />
          <path d="M10 14h4" />
          <path d="M10 18h4" />
        </svg>
      );
    case "dash":
    case "home":
      // lucide:layout-dashboard
      return (
        <svg {...svgProps}>
          <rect width="7" height="9" x="3" y="3" rx="1" />
          <rect width="7" height="5" x="14" y="3" rx="1" />
          <rect width="7" height="9" x="14" y="12" rx="1" />
          <rect width="7" height="5" x="3" y="16" rx="1" />
        </svg>
      );
    case "gameboard":
    case "gameboard-library":
      // lucide:gamepad-2
      return (
        <svg {...svgProps}>
          <line x1="6" x2="10" y1="11" y2="11" />
          <line x1="8" x2="8" y1="9" y2="13" />
          <line x1="15" x2="15.01" y1="13" y2="13" />
          <line x1="18" x2="18.01" y1="11" y2="11" />
          <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" />
        </svg>
      );
    case "bad-guy":
      // lucide:swords
      return (
        <svg {...svgProps}>
          <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
          <line x1="13" x2="19" y1="19" y2="13" />
          <line x1="16" x2="20" y1="16" y2="20" />
          <line x1="19" x2="21" y1="21" y2="19" />
          <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
          <line x1="5" x2="9" y1="14" y2="18" />
          <line x1="7" x2="4" y1="17" y2="20" />
          <line x1="3" x2="5" y1="19" y2="21" />
        </svg>
      );
    case "company-metrics":
      // lucide:line-chart
      return (
        <svg {...svgProps}>
          <path d="M3 3v18h18" />
          <path d="m19 9-5 5-4-4-3 3" />
        </svg>
      );
    case "team-room":
      // lucide:users
      return (
        <svg {...svgProps}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "metrics":
    case "workspace-kpi":
      // lucide:gauge
      return (
        <svg {...svgProps}>
          <path d="m12 14 4-4" />
          <path d="M3.34 19a10 10 0 1 1 17.32 0" />
        </svg>
      );
    case "settings":
      // lucide:settings (gear)
      return (
        <svg {...svgProps}>
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "hub":
      // lucide:layers
      return (
        <svg {...svgProps}>
          <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
          <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
          <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
        </svg>
      );
    case "scorekeeper":
    case "scorecard":
      // lucide:clipboard-list
      return (
        <svg {...svgProps}>
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="M12 11h4" />
          <path d="M12 16h4" />
          <path d="M8 11h.01" />
          <path d="M8 16h.01" />
        </svg>
      );
    case "claims":
      // lucide:shield-check
      return (
        <svg {...svgProps}>
          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "feed":
      // lucide:newspaper
      return (
        <svg {...svgProps}>
          <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
          <path d="M18 14h-8" />
          <path d="M15 18h-5" />
          <path d="M10 6h8v4h-8V6Z" />
        </svg>
      );
    case "messages":
      // lucide:message-square
      return (
        <svg {...svgProps}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case "challenge-arena":
      // lucide:trophy
      return (
        <svg {...svgProps}>
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
      );
    case "affiliate-hub":
    case "affiliates":
      // lucide:handshake
      return (
        <svg {...svgProps}>
          <path d="m11 17 2 2a1 1 0 1 0 3-3" />
          <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
          <path d="m21 3 1 11h-2" />
          <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
          <path d="M3 4h8" />
        </svg>
      );
    case "levelup":
    case "level-up":
      // lucide:trending-up
      return (
        <svg {...svgProps}>
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
      );
    case "widgets":
    case "manage-widgets":
      // lucide:layout-template
      return (
        <svg {...svgProps}>
          <rect width="18" height="7" x="3" y="3" rx="1" />
          <rect width="9" height="7" x="3" y="14" rx="1" />
          <rect width="5" height="7" x="16" y="14" rx="1" />
        </svg>
      );
    default:
      return null;
  }
};

const SETTINGS_SUB_ITEMS = [
  { id: "company-details", label: "Company Setup", route: "/settings/company-details" },
  { id: "players", label: "Player Management", route: "/settings/players" },
  { id: "level-up", label: "Level Up", route: "/settings/level-up" },
  { id: "tv-view", label: "TV View", route: "/settings/tv-view" },
  { id: "manage-widgets", label: "Manage Widgets", route: "/widgets" },
];

function SidebarNavItem({
  id,
  label,
  badge,
  isDropdownParent,
  indent,
  routePath,
  primeOnly,
  affiliateOnly,
  canShow,
  routeByKey,
  activePath,
  isSettingsChildActive,
  isSettingsOpen,
  isCollapsed,
  onToggleSettings,
  onNav,
}) {
  if (!canShow(id, { primeOnly, affiliateOnly })) return null;
  const path = routePath || ID_TO_ROUTE[id] || routeByKey[id];
  if (!path && !isDropdownParent) return null;
  const isActive = activePath === id || (isDropdownParent && isSettingsChildActive);

  const handleClick = () => {
    if (isDropdownParent) onToggleSettings();
    else onNav(path);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  /* Nav item: aiDesign exact — px-3 py-2 rounded-xl, label 13px font-medium, no theme */
  return (
    <div
      className={`flex items-center px-3 py-2 rounded-xl cursor-pointer transition-all duration-200 ${indent ? "ml-2.5" : ""} ${
        isActive ? "bg-white/10 text-white" : "text-white/85 hover:bg-white/5 hover:text-white"
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {RENDER_NAV_ICON(id, isActive)}
      {!isCollapsed && (
        <div className="ml-3 flex-grow flex items-center justify-between overflow-hidden">
          <span className="font-medium text-[13px] truncate">{label}</span>
          <div className="flex items-center space-x-2">
            {badge !== undefined && badge !== null && (
              <span className="bg-orange-500 text-white text-[9.5px] font-medium px-1.5 py-px rounded-full min-w-[16px] h-[16px] inline-flex items-center justify-center leading-none">
                {badge}
              </span>
            )}
            {isDropdownParent && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`w-3 h-3 transition-transform duration-300 ${isSettingsOpen ? "rotate-180" : ""}`}>
                <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarWorkspaceSelector({
  workspaceSelectorRef,
  dropdownPortalRef,
  isCollapsed,
  workspace,
  workspaces,
  subworkspaces = {},
  subheader,
  brandName,
  firstName,
  lastName,
  isWorkspaceSelectorOpen,
  onWorkspaceSelectorToggle,
  onSelectWorkspace,
  isTopPosition,
  isAdmin,
  isMobileView,
}) {
  const [expandedIds, setExpandedIds] = useState({});
  const selectorBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 300, compact: false, arrowOffset: 20, placement: "side" });
  const workspaceList = Array.isArray(workspaces) ? workspaces : [];
  const currentWs = workspace?.name ? { id: workspace._id || workspace.id, name: workspace.name, icon: workspace?.image ? GET_IMAGE(workspace.image) : "" } : null;

  const DROPDOWN_GAP = 12;

  useLayoutEffect(() => {
    if (isWorkspaceSelectorOpen && selectorBtnRef.current) {
      const rect = selectorBtnRef.current.getBoundingClientRect();
      const compact = window.innerWidth < 640;

      if (compact) {
        // The mobile sidebar overlay can span up to 85vw, so there's rarely
        // enough room beside it for a side-anchored card — forcing one to
        // fit there just pushes it back over the trigger it's meant to sit
        // next to (covering the selected item). Anchor below the trigger
        // instead, sized to the viewport, so it never overlaps the sidebar.
        const width = Math.min(260, window.innerWidth - DROPDOWN_GAP * 2);
        const left = Math.max(
          DROPDOWN_GAP,
          Math.min(rect.left, window.innerWidth - width - DROPDOWN_GAP)
        );
        const top = rect.bottom + DROPDOWN_GAP;
        const arrowOffset = Math.min(
          Math.max(20, rect.left + rect.width / 2 - left),
          width - 20
        );
        setDropdownPos({ top, left, width, compact, arrowOffset, placement: "bottom" });
        return;
      }

      // Desktop: side-anchor to the right of the sidebar, top-aligned with
      // the trigger (not vertically centered — centering let the card grow
      // upward past the button and cover the nav item above it).
      const width = 300;
      const left = Math.max(
        DROPDOWN_GAP,
        Math.min(rect.right + DROPDOWN_GAP, window.innerWidth - width - DROPDOWN_GAP)
      );
      // Keep at least DROPDOWN_GAP of breathing room from the viewport's top
      // edge — when the trigger is scrolled up near (or above) the top of
      // the screen, using rect.top as-is pinned the card flush against the
      // very top of the viewport, crowding the notch right into the card's
      // rounded corner. The notch offset is recomputed off however much the
      // card's top actually got pushed down, so it still points at the
      // trigger's real center (clamped to stay clear of that rounded corner).
      const top = Math.max(DROPDOWN_GAP, rect.top);
      const arrowOffset = Math.max(20, rect.top + rect.height / 2 - top);
      setDropdownPos({ top, left, width, compact, arrowOffset, placement: "side" });
    }
  }, [isWorkspaceSelectorOpen]);

  const [wsDialogOpen, setWsDialogOpen] = useState(false);
  const [subWsDialogOpen, setSubWsDialogOpen] = useState(false);
  const [editWorkspace, setEditWorkspace] = useState(null);
  const [editSubWorkspace, setEditSubWorkspace] = useState(null);
  const [parentWorkspace, setParentWorkspace] = useState(null);

  const handleAddWorkspace = (e) => {
    e.stopPropagation();
    setEditWorkspace(null);
    setWsDialogOpen(true);
  };

  const handleEditWorkspace = (e, ws) => {
    e.stopPropagation();
    setEditWorkspace(ws);
    setWsDialogOpen(true);
  };

  const handleAddSubWorkspace = (e, ws) => {
    e.stopPropagation();
    setParentWorkspace(ws);
    setEditSubWorkspace(null);
    setSubWsDialogOpen(true);
  };

  const handleEditSubWorkspace = (e, subWs, parentWs) => {
    e.stopPropagation();
    setEditSubWorkspace(subWs);
    setParentWorkspace(parentWs);
    setSubWsDialogOpen(true);
  };

  const toggleExpanded = (e, wsId, ws) => {
    e.stopPropagation();
    if (ws) setParentWorkspace(ws);
    setExpandedIds((prev) => ({ ...prev, [wsId]: !prev[wsId] }));
  };

  const isSelected = (ws) =>
    String(workspace?._id || workspace?.id || "") === String(ws._id || ws.id || "");

  const handleWorkspacePick = (ws) => {
    if (!ws) return;
    /* Container / group folder: select it (empty main view) and expand so
       Field Sales / Inside Sales are one click away. */
    if (ws.isContainer) {
      setExpandedIds((prev) => ({ ...prev, [ws._id]: true }));
    }
    onSelectWorkspace(ws);
  };

  /* Workspace selector: aiDesign — px-2 rounded-xl h-full (top) or h-9 (inline), no theme */
  return (
    <div className={`relative ${!isTopPosition ? "px-1" : ""}`} ref={workspaceSelectorRef}>
      <div
        ref={selectorBtnRef}
        className={`flex items-center justify-between w-full cursor-pointer hover:bg-white/5 transition-all ${
          isTopPosition ? "px-2 py-0 rounded-xl h-full" : "py-1 px-2 bg-white/5 border border-white/5 rounded-md h-9"
        }`}
        onClick={() => !isCollapsed && workspaceList.length > 0 && onWorkspaceSelectorToggle()}
        role="button"
        tabIndex={0}
      >
        <div className={`flex items-center overflow-hidden ${isTopPosition ? "space-x-2.5" : "space-x-2"}`}>
          <div className={`bg-white/20 flex-shrink-0 flex items-center justify-center border border-white/20 p-1 shadow-sm ${isTopPosition ? "w-10 h-10 rounded-lg" : "w-6 h-6 rounded-md"}`}>
            {currentWs?.icon ? (
              <img src={currentWs.icon} alt={currentWs.name} className="w-full h-full object-contain rounded-md" />
            ) : (
              <span className="text-white text-xs font-bold">W</span>
            )}
          </div>
          {!isCollapsed && (
            <div className="flex flex-col truncate">
              {isTopPosition && <span className="font-bold text-sm tracking-tight truncate text-white">{firstName && lastName ? `${firstName} ${lastName}` : brandName}</span>}
              <span className={`truncate ${isTopPosition ? "text-indigo-300/70 text-[11px] font-bold" : "text-white text-[12px] font-medium"}`}>
                {currentWs?.name || subheader || "Workspace"}
              </span>
            </div>
          )}
        </div>
        {!isCollapsed && workspaceList.length > 0 && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`w-3 h-3 text-indigo-100 transition-transform duration-300 ${isWorkspaceSelectorOpen ? "rotate-180" : ""}`}>
            <path d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>

      {isWorkspaceSelectorOpen && !isCollapsed && workspaceList.length > 0 && ReactDOM.createPortal(
        <div
          ref={dropdownPortalRef}
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl border border-gray-100"
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 99999,
            paddingTop: dropdownPos.compact ? "6px" : "8px",
            paddingBottom: dropdownPos.compact ? "6px" : "8px",
          }}
        >
          <div
            className={`absolute bg-white border-gray-100 ${dropdownPos.placement === "bottom" ? "border-t border-l" : "border-l border-b"}`}
            style={
              dropdownPos.placement === "bottom"
                ? { top: "-6px", left: dropdownPos.arrowOffset - 6, width: "12px", height: "12px", transform: "rotate(45deg)" }
                : { top: dropdownPos.arrowOffset - 6, left: "-6px", width: "12px", height: "12px", transform: "rotate(45deg)" }
            }
          />
          <div className={`text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1 ${dropdownPos.compact ? "px-3 py-1.5" : "px-4 py-2"}`}>My Workspaces</div>
          <div className={`overflow-y-auto ${dropdownPos.compact ? "max-h-[260px]" : "max-h-[320px]"}`}>
            {workspaceList.map((ws) => {
              const subItems = subworkspaces[ws._id];
              const hasSubs = Array.isArray(subItems) && subItems.length > 0;
              const isExpanded = expandedIds[ws._id];
              const showExpand = hasSubs || isAdmin;
              return (
                <div key={ws._id || ws.id}>
                  <div className="flex items-center w-full">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        handleWorkspacePick(ws);
                      }}
                      className={`flex-1 flex items-center hover:bg-indigo-50 transition-colors group text-left min-w-0 ${isSelected(ws) ? "bg-indigo-50/50" : ""} ${dropdownPos.compact ? "px-3 py-2" : "px-4 py-2.5"}`}
                    >
                      <div className={`rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 bg-gray-100 ${dropdownPos.compact ? "w-6 h-6 mr-2" : "w-7 h-7 mr-3"}`}>
                        {ws?.image ? <img src={GET_IMAGE(ws.image)} alt={ws.name} className="w-full h-full object-cover" /> : <span className="text-gray-400 text-xs flex items-center justify-center w-full h-full">W</span>}
                      </div>
                      <span className={`font-semibold text-gray-700 group-hover:text-indigo-900 truncate ${dropdownPos.compact ? "text-[11px]" : "text-xs"}`}>{ws.name}</span>
                      {!!ws.isContainer && (
                        <span className="ml-1.5 flex-shrink-0 text-[9px] font-bold uppercase tracking-wide text-indigo-500 bg-indigo-50 border border-indigo-100 rounded px-1 py-0.5">Group</span>
                      )}
                      {isSelected(ws) && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3 h-3 text-indigo-500 flex-shrink-0 ml-1">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                    {showExpand && (
                      <button
                        type="button"
                        onClick={(e) => toggleExpanded(e, ws._id, ws)}
                        className={`text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors ${dropdownPos.compact ? "p-1.5" : "p-2"}`}
                        aria-label={isExpanded ? "Collapse sub-workspaces" : "Expand sub-workspaces"}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}>
                          <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="pl-4 pr-2 pb-1 border-l-2 border-gray-100 ml-5 mr-2 mb-1">
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-0.5">{ws.name} Sub-Workspaces</div>
                      {!hasSubs ? (
                        <p className="text-[10px] text-gray-400 px-1 py-1">No sub-workspaces yet. Click below to add one.</p>
                      ) : (
                        subItems.map((subWs) => (
                          <div key={subWs._id} className="flex items-center group/sub">
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                if (e.button !== 0) return;
                                e.preventDefault();
                                handleWorkspacePick(subWs);
                              }}
                              className={`flex-1 flex items-center px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-left min-w-0 ${isSelected(subWs) ? "bg-indigo-50/50" : ""}`}
                            >
                              <div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0 mr-2 border border-gray-100 bg-gray-100">
                                {subWs?.image ? <img src={GET_IMAGE(subWs.image)} alt={subWs.name} className="w-full h-full object-cover" /> : <span className="text-gray-400 text-[10px] flex items-center justify-center w-full h-full">W</span>}
                              </div>
                              <span className="text-xs font-medium text-gray-600 group-hover:text-indigo-900">{subWs.name}</span>
                              {isSelected(subWs) && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3 h-3 text-indigo-500 flex-shrink-0 ml-1">
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                            {isAdmin && !isMobileView && (
                              <button
                                type="button"
                                onClick={(e) => handleEditSubWorkspace(e, subWs, ws)}
                                className="p-1.5 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 rounded opacity-0 group-hover/sub:opacity-100 transition-all flex-shrink-0"
                                aria-label="Edit sub-workspace"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))
                      )}
                      {isAdmin && !dropdownPos.compact && (
                        <>
                          <div className="my-1 border-t border-gray-100" />
                          <button
                            type="button"
                            onClick={(e) => handleAddSubWorkspace(e, ws)}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-left"
                          >
                            <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5 text-indigo-600">
                                <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <span className="text-[10px] font-medium text-indigo-600">Add sub-workspace</span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleEditWorkspace(e, ws)}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-left"
                          >
                            <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5 text-indigo-600">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <span className="text-[10px] font-medium text-indigo-600">Edit workspace</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {isAdmin && (
              <>
                <div className={`my-1 border-t border-gray-100 ${dropdownPos.compact ? "mx-3" : "mx-4"}`} />
                <button
                  type="button"
                  onClick={handleAddWorkspace}
                  className={`w-full flex items-center gap-2 hover:bg-indigo-50 transition-colors text-left ${dropdownPos.compact ? "px-3 py-2" : "px-4 py-2.5"}`}
                >
                  <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 text-indigo-600">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className={`font-medium text-indigo-600 ${dropdownPos.compact ? "text-[11px]" : "text-xs"}`}>Add new workspace</span>
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      <WorkspaceDialog
        open={wsDialogOpen}
        setOpen={setWsDialogOpen}
        onEnd={() => { setWsDialogOpen(false); setEditWorkspace(null); }}
        action={editWorkspace ? WORKSPACE_ACTION.UPDATE : WORKSPACE_ACTION.CREATE}
        group={editWorkspace}
      />
      <SubWorkspaceDialog
        open={subWsDialogOpen}
        setOpen={setSubWsDialogOpen}
        onEnd={() => { setSubWsDialogOpen(false); setEditSubWorkspace(null); }}
        action={editSubWorkspace ? SUB_WORKSPACE_ACTION.UPDATE : SUB_WORKSPACE_ACTION.CREATE}
        group={editSubWorkspace}
        parentWorkspace={parentWorkspace}
      />
    </div>
  );
}


// Helper: returns true if any ids in .items confObj are visible (for header display logic)
function hasVisibleNavItems(items, canShow, extraProps = {}) {
  if (!items || items.length === 0) return false;
  return items.some(({ id, primeOnly, affiliateOnly, minSubscription }) =>
    canShow(id, { primeOnly, affiliateOnly, minSubscription, ...extraProps })
  );
}

function Sidebar({
  isCollapsed: isCollapsedProp,
  toggleCollapse,
  isMobileOpen = false,
  isMobileView = false,
  onMobileClose,
  activePath,
  onNavigate,
  routes,
  badges = {},
  brandLogo,
  brandName,
  subheader,
  profileSlot,
  headerMenuSlot,
  subscriptionLevel,
  access,
}) {
  // The desktop collapse/expand toggle persists (via localStorage in Main)
  // across the whole session, including into mobile. On mobile the sidebar
  // is a full off-canvas overlay, not an in-flow rail — collapsing there
  // just leaves a narrow icon strip with no visible way to expand it again
  // (the collapse toggle button below is desktop-only), stranding the
  // profile/settings items. Mobile always renders expanded regardless of
  // whatever the desktop toggle last left in storage.
  const isCollapsed = isMobileView ? false : isCollapsedProp;
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // Analytics (Metrics/Dashboards) dropdown — ALWAYS starts closed so the
  // "Dashboards" child never sits next to the Workspace "Dashboard" link.
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isWorkspaceSelectorOpen, setIsWorkspaceSelectorOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  // Help center card: collapsed to a single row on web (room is tight),
  // expanded with the description + CTA by default on mobile (more room,
  // and no hover affordance to hint it's expandable) — same markup either
  // way, just its starting state. Still user-toggleable via the chevron.
  const [isHelpCenterOpen, setIsHelpCenterOpen] = useState(isMobileView);
  const workspaceSelectorRef = useRef(null);
  const wsDropdownRef = useRef(null);
  const footerCardRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Which Analytics child is active (only meaningful on /company-metrics).
  const analyticsTab = new URLSearchParams(location.search).get("tab") === "dashboards" ? "dashboards" : "metrics";

  const workspace = UseSelectedWorkspace();
  // Workspace options for selector come from company hook (Redux workspacesReducer)
  const workspaces = UseCompanyWorkspaces();
  const subworkspaces = UseCompanySubWorkspaces();
  const { primaryColor: companyPrimaryColor } = UseCompanyColors();
  // Same theme.palette.primary the Hub tabs/toggles pull their color from
  // (generated from this company's primary color in theme.js) — used below
  // so the profile card's avatar-fallback and role label match those
  // toggles instead of a hardcoded Tailwind indigo unrelated to it.
  const theme = useTheme();
  const profileAccentColor = theme.palette.primary.focus;
  const { image, firstName, lastName, isAdmin: isTrueAdmin } = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const isPrime = UseIsPrime();
  const isAffiliate = UseIsAffiliate();
  const unclaimed = UseUnclaimedCount();
  const effectiveBadges = { ...badges, claims: badges.claims ?? unclaimed };

  const isSettingsChildActive = ["company-details", "players", "level-up", "tv-view", "manage-widgets"].includes(activePath);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inSelector = workspaceSelectorRef.current && workspaceSelectorRef.current.contains(e.target);
      const inDropdown = wsDropdownRef.current && wsDropdownRef.current.contains(e.target);
      if (!inSelector && !inDropdown) {
        setIsWorkspaceSelectorOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (footerCardRef.current && !footerCardRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleNav = (pathOrKey) => {
    const path = pathOrKey.startsWith("/") ? pathOrKey : ID_TO_ROUTE[pathOrKey] || `/${pathOrKey}`;
    onNavigate(path);
    navigate(path);
    if (isMobileView && onMobileClose) onMobileClose();
  };

  const routeByKey = {};
  routes.forEach((r) => {
    if (r.key) routeByKey[r.key] = r.route?.startsWith("/") ? r.route : `/${r.route || r.key}`;
    if (r.collapse) r.collapse.forEach((s) => { if (s.key) routeByKey[s.key] = s.route?.startsWith("/") ? s.route : `/${s.route || s.key}`; });
  });

  const MAX_LEVEL_DEMO = 99;
  const canShow = (key, item) => {
    if (key === "company-view" && subscriptionLevel === MAX_LEVEL_DEMO && !isPrime) return false;
    if (item?.primeOnly && !isPrime) return false;
    if (item?.affiliateOnly && !isAffiliate) return false;
    if (item?.minSubscription != null && subscriptionLevel < item.minSubscription) return false;
    if (access && access[key] === false) return false;
    return true;
  };

  const handleSelectWorkspace = (ws) => {
    dispatch(setSelectedWorkspace({ selectedWorkspace: ws }));
    setIsWorkspaceSelectorOpen(false);
  };

  const navBgColor = companyPrimaryColor || "#1E1B4B";
  const isOverlay = isMobileView && typeof onMobileClose === "function";
  const showSidebar = !isMobileView || isMobileOpen;
  const collapseAriaLabel = isCollapsed ? "Expand sidebar" : "Collapse sidebar";

  const sidebarCollapseToggle = (
    <div
      className="absolute pointer-events-none right-0 top-24 translate-x-1/2"
      style={{ zIndex: 100000 }}
    >
      <button
        type="button"
        onClick={() => toggleCollapse?.()}
        className="pointer-events-auto w-6 h-6 bg-white border border-gray-100 rounded-full flex items-center justify-center text-indigo-900 hover:scale-110 transition-transform shadow-md"
        aria-label={collapseAriaLabel}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className={`w-3 h-3 transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`}
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    </div>
  );

  // --- New Section: Sidebar groupings for visibility logic ---
  // We need to define which nav items belong to each section for checking visibility.
  // This is replicated/adapted for both admin and player views.

  // For admin
  // Three clean groups: Company (admin config), Workspace (operational),
  // Activities (collab). Old layout had "My Game" between Workspace and a
  // duplicate "Company" — Gameboard/Bad Guy/Metrics now continue under
  // Workspace, Settings moves up to Company, Hub/Scorekeeper/Claims move
  // to Workspace as the workspace-level operations they actually are.
  const adminSectionConfig = [
    {
      label: "Company",
      items: [
        { id: "company-view", primeOnly: true },
        { id: "company-metrics" },
        { id: "settings" }
      ]
    },
    {
      label: "Workspace",
      items: [
        { id: "dash" },
        { id: "gameboard" },
        { id: "bad-guy" },
        { id: "metrics" },
        { id: "hub" },
        { id: "scorekeeper" },
        { id: "claims" }
      ],
      hasWorkspaceSelector: true
    },
    {
      label: "Activities",
      items: [
        { id: "feed" },
        { id: "messages", primeOnly: true },
        { id: "challenge-arena", primeOnly: true }
      ]
    },
    {
      label: "System",
      items: [
        { id: "affiliates", affiliateOnly: true }
      ]
    }
  ];

  // For player
  const playerSectionConfig = [
    {
      label: "My Game",
      items: [
        { id: "scorecard" },
        { id: "hub" },
        { id: "claims" },
        { id: "levelup", primeOnly: true }
      ]
    },
    {
      label: "Activities",
      items: [
        { id: "feed" },
        { id: "messages", primeOnly: true },
        { id: "challenge-arena", primeOnly: true },
        { id: "widgets", primeOnly: true }
      ]
    },
    {
      label: "System",
      items: [
        { id: "affiliates", affiliateOnly: true }
      ]
    }
  ];

  // For admin, calculate "Workspace" section visibility:
  function isWorkspaceSectionVisible() {
    // Workspace selector is shown whenever there are workspaces, regardless of active route
    const showSelector = Array.isArray(workspaces) && workspaces.length > 0;
    // Dash must be visible to count
    const dashVisible = canShow("dash", {});
    return showSelector || dashVisible;
  }

  // ------ sidebarContent render ------
  const sidebarContent = (
    <div
      className="relative h-full min-h-screen text-white flex flex-col transition-all duration-300 ease-in-out border-r border-indigo-900/50 flex-shrink-0 w-full"
      style={{
        width: isCollapsed ? "5.25rem" : "14rem",
        minWidth: isCollapsed ? "5.25rem" : "14rem",
        maxWidth: isMobileView ? "min(20rem, 85vw)" : "20rem",
        backgroundColor: navBgColor,
      }}
    >
      <div className="p-4 h-20 border-b border-white/5 relative flex items-center">
        {isOverlay && (
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Close sidebar"
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        )}
        {isAdmin ? (
          <div className="flex items-center space-x-3 w-full h-full px-2">
            <div className="w-10 h-10 border-2 border-white/20 overflow-hidden flex-shrink-0 shadow-sm">
              {image ? <img src={GET_IMAGE(image)} alt="Profile" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: profileAccentColor }}>{firstName?.[0]}{lastName?.[0]}</div>}
            </div>
            {!isCollapsed && (
              <div className="flex flex-col truncate">
                <span className="font-medium text-sm tracking-tight truncate text-white">{firstName && lastName ? `${firstName} ${lastName}` : brandName}</span>
                <span className="text-[11px] text-white/55 font-normal truncate">Admin</span>
              </div>
            )}
            {/* {!isCollapsed && headerMenuSlot && <div className="ml-auto">{headerMenuSlot}</div>} */}
          </div>
        ) : (
          <div className="flex items-center space-x-3 w-full h-full px-2">
            <div className="w-10 h-10 border-2 border-white/20 overflow-hidden flex-shrink-0 shadow-sm">
              {image ? <img src={GET_IMAGE(image)} alt="Profile" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: profileAccentColor }}>{firstName?.[0]}{lastName?.[0]}</div>}
            </div>
            {!isCollapsed && (
              <div className="flex flex-col truncate">
                <span className="font-medium text-sm tracking-tight truncate text-white">{firstName && lastName ? `${firstName} ${lastName}` : brandName}</span>
                <span className="text-[11px] text-white/55 font-normal truncate">Player</span>
              </div>
            )}
            {/* {!isCollapsed && headerMenuSlot && <div className="ml-auto">{headerMenuSlot}</div>} */}
          </div>
        )}
      </div>

      <div className="flex-grow overflow-y-auto px-3 pb-4 overflow-x-hidden custom-scrollbar">
        {isAdmin ? (
          <>
            {/* --- ADMIN: Company Section --- */}
            {(hasVisibleNavItems([{ id: "company-view", primeOnly: true }, { id: "company-metrics" }], canShow)) && (
              <>
                <SECTION_HEADER label="Company" isCollapsed={isCollapsed} />
                <div>
                  <SidebarNavItem id="company-view" label="Company View" routePath="/company-view" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  {canShow("workspace-kpi", {}) && (
                    <>
                      {/* Analytics — dropdown holding Metrics + Dashboards. Always
                          starts closed (so its "Dashboards" never shows beside
                          Workspace "Dashboard"), but stays open once expanded —
                          picking Metrics/Dashboards must not collapse it back. */}
                      <SidebarNavItem id="company-metrics" label="Analytics" isDropdownParent canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={activePath === "company-metrics"} isSettingsOpen={isAnalyticsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsAnalyticsOpen((v) => !v)} onNav={handleNav} />
                      {isAnalyticsOpen && !isCollapsed && (
                        <div className="ml-5 mt-1 border-l-2 border-white/10 pl-3">
                          {[
                            { id: "metrics", label: "Metrics", route: "/company-metrics?tab=metrics" },
                            { id: "dashboards", label: "Dashboards", route: "/company-metrics?tab=dashboards" },
                          ].map((sub) => {
                            const subActive = activePath === "company-metrics" && analyticsTab === sub.id;
                            return (
                              <div
                                key={sub.id}
                                onClick={() => handleNav(sub.route)}
                                className={`px-3 py-2 rounded-xl cursor-pointer text-[12px] font-medium transition-colors ${subActive ? "text-white" : "text-white/70 hover:text-white"}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleNav(sub.route); } }}
                              >
                                {sub.label}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {/* --- ADMIN: Workspace Section — Dashboard + (former My Game items continue here) --- */}
            {isWorkspaceSectionVisible() && (
              <>
                <SECTION_HEADER label="Workspace" isCollapsed={isCollapsed} />
                <div className={`transition-all duration-200 ease-in-out ${isCollapsed ? "-mt-10" : ""} opacity-100 translate-y-0 mb-3 h-9`}>
                  <SidebarWorkspaceSelector workspaceSelectorRef={workspaceSelectorRef} dropdownPortalRef={wsDropdownRef} isCollapsed={isCollapsed} workspace={workspace} workspaces={workspaces} subworkspaces={subworkspaces} subheader={subheader} brandName={brandName} firstName={firstName} lastName={lastName} isWorkspaceSelectorOpen={isWorkspaceSelectorOpen} onWorkspaceSelectorToggle={() => setIsWorkspaceSelectorOpen((v) => !v)} onSelectWorkspace={handleSelectWorkspace} isTopPosition={false} isAdmin={isAdmin} isMobileView={isMobileView} />
                </div>
                <div className="space-y-0.5 mb-2">
                  <SidebarNavItem id="dash" label="Dashboard" routePath="/home" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="metrics" label="Metrics" routePath="/workspace-kpi" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="gameboard" label="Gameboard" routePath="/gameboard-library" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="bad-guy" label="Bad Guy" routePath="/bad-guy" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="team-room" label="Team room" routePath="/team-room" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}

            {/* --- ADMIN: Company (lower) section for Settings etc. --- */}
            {hasVisibleNavItems(
              [
                { id: "settings" },
                { id: "hub" },
                { id: "scorekeeper" },
                { id: "claims" }
              ],
              canShow
            ) && (
              <>
                <SECTION_HEADER label="Company" isCollapsed={isCollapsed} />
                <div
                  className={`space-y-0.5 mb-2 ${
                    isCollapsed ? "-mt-12" : ""
                  }`}
                >
                  <SidebarNavItem id="settings" label="Settings" isDropdownParent canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  {isSettingsOpen && !isCollapsed && (
                    <div className="ml-5 mt-1 border-l-2 border-white/10 pl-3 ">
                      {SETTINGS_SUB_ITEMS.map((sub) => {
                        if (sub.id === "level-up" && !isPrime) return null;
                        if (sub.id === "tv-view" && !isPrime) return null;
                        if (sub.id === "manage-widgets" && !isPrime) return null;
                        return (
                          <div
                            key={sub.id}
                            onClick={() => handleNav(sub.route)}
                            className={`px-3 py-2 rounded-xl cursor-pointer text-[12px] font-medium transition-colors ${activePath === sub.id ? "text-white" : "text-white/70 hover:text-white"}`}
                            role="button"
                            tabIndex={0}
                          >
                            {sub.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <SidebarNavItem id="hub" label="Hub" routePath="/hub" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="scorekeeper" label="Scorekeeper" routePath="/scorekeeper" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="claims" label="Claims" badge={effectiveBadges.claims} routePath="/claims" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}

            {/* --- ADMIN: Activities --- */}
            {hasVisibleNavItems(
              [
                { id: "feed" },
                { id: "messages", primeOnly: true },
                { id: "challenge-arena", primeOnly: true }
              ],
              canShow
            ) && (
              <>
                <SECTION_HEADER label="Activities" isCollapsed={isCollapsed} />
                <div
                  className={`${
                    isCollapsed ? "-mt-12" : ""
                  }`}
                >
                  <SidebarNavItem id="feed" label="Feed" routePath="/feed" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="messages" label="Messages" routePath="/messages" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="challenge-arena" label="Challenge Arena" routePath="/challenge-arena" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}

            {/* --- ADMIN: System --- */}
            {hasVisibleNavItems(
              [{ id: "affiliates", affiliateOnly: true }],
              canShow
            ) && (
              <>
                <SECTION_HEADER label="System" isCollapsed={isCollapsed} />
                <div
                  className={`${
                    isCollapsed ? "-mt-12" : ""
                  }`}
                >
                  <SidebarNavItem id="affiliates" label="Affiliate Hub" routePath="/affiliates" affiliateOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* PLAYER: My Dashboard (no section header, only shows if visible) */}
            {canShow("home", {}) && (
              <div className="mt-2">
                <SidebarNavItem id="home" label="My Dashboard" routePath="/home" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
              </div>
            )}

            {/* PLAYER: My Game */}
            {hasVisibleNavItems(
              [
                { id: "scorecard" },
                { id: "hub" },
                { id: "claims" },
                { id: "levelup", primeOnly: true }
              ],
              canShow
            ) && (
              <>
                <SECTION_HEADER label="My Game" isCollapsed={isCollapsed} />
                <div>
                  <SidebarNavItem id="scorecard" label="Scorecard" routePath="/scorekeeper/timeline?player=true" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="hub" label="Hub" routePath="/hub" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="claims" label="Claims" badge={effectiveBadges.claims} routePath="/claims" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="levelup" label="Level Up" routePath="/levelup" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}

            {/* PLAYER: Activities */}
            {hasVisibleNavItems(
              [
                { id: "feed" },
                { id: "messages", primeOnly: true },
                { id: "challenge-arena", primeOnly: true },
                { id: "widgets", primeOnly: true }
              ],
              canShow
            ) && (
              <>
                <SECTION_HEADER label="Activities" isCollapsed={isCollapsed} />
                <div className="">
                  <SidebarNavItem id="feed" label="Feed" routePath="/feed" canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="messages" label="Messages" routePath="/messages" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="challenge-arena" label="Challenge Arena" routePath="/challenge-arena" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                  <SidebarNavItem id="widgets" label="Manage Widgets" routePath="/widgets" primeOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}

            {/* PLAYER: System */}
            {hasVisibleNavItems(
              [{ id: "affiliates", affiliateOnly: true }],
              canShow
            ) && (
              <>
                <SECTION_HEADER label="System" isCollapsed={isCollapsed} />
                <div className="">
                  <SidebarNavItem id="affiliates" label="Affiliate Hub" routePath="/affiliates" affiliateOnly canShow={canShow} routeByKey={routeByKey} activePath={activePath} isSettingsChildActive={isSettingsChildActive} isSettingsOpen={isSettingsOpen} isCollapsed={isCollapsed} onToggleSettings={() => setIsSettingsOpen((v) => !v)} onNav={handleNav} />
                </div>
              </>
            )}
          </>
        )}

        {!isCollapsed && (
          <div className="mt-6 mx-1">
            <div className="h-px mb-3 bg-white/10" />
            <div className="bg-white/10 rounded-2xl overflow-hidden">
              <div
                onClick={() => setIsHelpCenterOpen((v) => !v)}
                className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-white/5 transition-colors"
                role="button"
                tabIndex={0}
                aria-expanded={isHelpCenterOpen}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsHelpCenterOpen((v) => !v); } }}
              >
                <div className="w-6 h-6 bg-white/15 rounded-full flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0">?</div>
                <span className="flex-grow text-[13px] font-bold text-white truncate">Help center</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className={`w-3 h-3 text-white/60 flex-shrink-0 transition-transform duration-200 ${isHelpCenterOpen ? "" : "-rotate-90"}`}
                >
                  <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {isHelpCenterOpen && (
                <div className="px-3 pb-3">
                  <p className="text-[11px] text-indigo-200 leading-relaxed mb-3">
                    Reach our support team for anything tracking or scoring related.
                  </p>
                  <button
                    type="button"
                    onClick={() => window.open("https://helpcenter.gamemybiz.com/", "_blank")}
                    className="w-full py-2.5 bg-white text-indigo-950 rounded-xl text-xs font-bold hover:bg-indigo-50 transition-colors"
                  >
                    Go to help center
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer: aiDesign exact — p-4, font-bold text-[13px], text-[10px] */}
      <div className="p-3 border-t border-white/5 relative" ref={footerCardRef}>
          {/* Rendered in normal flow (absolutely positioned relative to this
              same, untransformed footer) instead of a portaled MUI Popover —
              the mobile drawer that houses this footer animates itself with
              a CSS transform, which was throwing off the popover's own
              viewport-based math and popping the menu up near the top of
              the page instead of right above the trigger. */}
          {!isCollapsed && isProfileMenuOpen && (
            <div className="absolute left-3 right-3 bottom-[calc(100%+8px)] rounded-xl overflow-hidden border border-white/10 shadow-xl z-10" style={{ backgroundColor: navBgColor }}>
              {(isAdmin ? adminFooterRoutes : playerFooterRoutes).map((route) => (
                <div
                  key={route.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setIsProfileMenuOpen(false); handleNav(route.route); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsProfileMenuOpen(false); handleNav(route.route); } }}
                  className="px-4 py-2.5 text-[13px] font-medium text-white/85 hover:bg-white/10 hover:text-white cursor-pointer transition-colors"
                >
                  {route.name}
                </div>
              ))}
            </div>
          )}
          <div
            onClick={() => !isCollapsed && setIsProfileMenuOpen((v) => !v)}
            className={`flex items-center justify-between gap-2 rounded-xl cursor-pointer hover:bg-white/5 transition-colors ${isCollapsed ? "p-1.5" : "px-3 py-2.5"}`}
          >
            <div className="flex items-center space-x-3 overflow-hidden">
              <div className="w-9 h-9 rounded-lg border border-white/20 overflow-hidden flex-shrink-0">
                {image ? <img src={GET_IMAGE(image)} alt="Profile" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: profileAccentColor }}>{firstName?.[0]}{lastName?.[0]}</div>}
              </div>
              {!isCollapsed && (
                <div className="flex flex-col truncate">
                  <span className="font-bold text-[13px] text-white truncate">{firstName || "User"} </span>
                  <span className="text-[10px] font-medium truncate" style={{ color: profileAccentColor }}>{isAdmin ? "Admin" : "Player"}</span>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`w-3.5 h-3.5 text-white/70 flex-shrink-0 transition-transform duration-200 ${isProfileMenuOpen ? "" : "rotate-180"}`}>
                <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.1); }
      `}</style>
    </div>
  );

  if (isOverlay) {
    return (
      <div
        className={`fixed inset-y-0 left-0 z-[9999] md:hidden overflow-visible transition-transform duration-300 ease-in-out ${showSidebar ? "translate-x-0" : "-translate-x-full"}`}
        style={{ pointerEvents: showSidebar ? "auto" : "none" }}
      >
        {sidebarContent}
      </div>
    );
  }

  return (
    <div
      className="relative z-30 h-screen sticky top-0 flex-shrink-0 overflow-visible transition-all duration-300 ease-in-out"
      style={{
        width: isCollapsed ? "5.25rem" : "14rem",
        minWidth: isCollapsed ? "5.25rem" : "14rem",
      }}
    >
      {sidebarContent}
      {sidebarCollapseToggle}
    </div>
  );
}

export default Sidebar;
